import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { v4 as uuid } from "uuid";
import { QueryStatus } from "../../app/types";
import { InputType, ReasonForDecline, SubmittedAnswer } from "../../API";
import {
  fetchAssessmentEvent,
  fetchPendingAssessmentsByRater,
  completeAssessmentRequest as completeAssessmentRequestAPI,
  declineAssessmentRequest as declineAssessmentRequestAPI,
  AssessmentDetails,
  QuestionAnswerOption,
  AnswerOption,
  PendingAssessmentResponse,
  AcgmeProcedureData,
  saveAssessmentProgressRequest,
  SaveAssessmentProgressResponse
} from "./assessmentEventsAPI";
import { List, ListItem } from "@mui/material";

export const useUsersPendingAssessmentEvents = (userId?: string) => {
  const [pendingAssessmentEvents, setPendingAssessmentEvents] =
    useState<PendingAssessmentResponse[]>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getPendingAssessmentEvents = async (userId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchPendingAssessmentsByRater(userId);
      setPendingAssessmentEvents(results ?? []);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error fetching pending assessments: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (userId) {
      getPendingAssessmentEvents(userId);
    }
  }, [userId]);

  return {
    pendingAssessmentEvents,
    status,
    error,
    getPendingAssessmentEvents
  };
};

export const useAssessmentEvent = (assessmentEventId?: string) => {
  const [assessmentDetails, setAssessmentDetails] =
    useState<AssessmentDetails | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getAssessmentDetails = async (assessmentEventId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchAssessmentEvent(assessmentEventId);
      setAssessmentDetails(result);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error fetching assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (assessmentEventId) {
      getAssessmentDetails(assessmentEventId);
    }
  }, [assessmentEventId]);

  return {
    assessmentDetails,
    status,
    error
  };
};

export const useDeclineAssessmentRequest = () => {
  const [status, setStatus] = useState<string>("idle");
  const [error, setError] = useState<string>();
  const [declinedAssessmentEvent, setDeclinedAssessmentEvent] = useState<
    string | null
  >(null);

  const declineAssessmentRequest = async (
    assessmentEventId: string,
    reason: ReasonForDecline
  ) => {
    setStatus("loading");
    setError(undefined);

    try {
      const result = await declineAssessmentRequestAPI(
        assessmentEventId,
        reason
      );
      setDeclinedAssessmentEvent(result?.message);
      setStatus(result?.status);
    } catch (error) {
      const errorString = `error declining assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  return {
    status,
    error,
    declinedAssessmentEvent,
    declineAssessmentRequest
  };
};

export const useCompleteAssessmentRequest = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();
  const [completedAssessmentEvent, setCompletedAssessmentEvent] =
    useState<AcgmeProcedureData | null>(null);

  const completeAssessmentRequest = async (
    assessmentEventId: string,
    answers: SubmittedAnswer[]
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await completeAssessmentRequestAPI(
        assessmentEventId,
        answers
      );
      setCompletedAssessmentEvent(result);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error completing assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  return {
    status,
    error,
    completedAssessmentEvent,
    completeAssessmentRequest
  };
};

export const useSaveAssessmentProgress = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();
  const [saveResult, setSaveResult] =
    useState<SaveAssessmentProgressResponse | null>(null);

  const saveAssessmentProgress = async (
    assessmentEventId: string,
    answers: SubmittedAnswer[]
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await saveAssessmentProgressRequest(assessmentEventId, answers);
      setSaveResult(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      const errorString = `error saving assessment progress: ${JSON.stringify(error)}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
      return null;
    }
  };

  return {
    status,
    error,
    saveResult,
    saveAssessmentProgress
  };
};

type UpdateAnswerSelectionsProps = {
  question: QuestionAnswerOption;
  currentAnswers: AnswerOption[];
  setAnswers: Dispatch<SetStateAction<AnswerOption[]>>;
  answerOption?: AnswerOption;
  stringValue?: string;
};
export const updateAnswerSelections = ({
  question,
  currentAnswers,
  setAnswers,
  answerOption,
  stringValue
}: UpdateAnswerSelectionsProps): AnswerOption[] => {
  const inputType = question.inputTypeName;
  if (inputType === InputType.Radio && answerOption) {
    const updatedAnswers = [...currentAnswers];
    const indexOfExistingAnswer = currentAnswers.findIndex(
      (answer) => answer.questionId === question.id
    );
    if (indexOfExistingAnswer > -1) {
      updatedAnswers.splice(indexOfExistingAnswer, 1);
    }
    updatedAnswers.push(answerOption);
    setAnswers(updatedAnswers);
    return updatedAnswers;
  }
  if (inputType === InputType.Checkbox && answerOption) {
    const updatedAnswers = [...currentAnswers];
    const indexOfExistingAnswer = currentAnswers.findIndex(
      (answer) =>
        answer.questionId === question.id && answer.value === answerOption.value
    );
    if (indexOfExistingAnswer > -1) {
      updatedAnswers.splice(indexOfExistingAnswer, 1);
    } else {
      updatedAnswers.push(answerOption);
    }
    setAnswers(updatedAnswers);
    return updatedAnswers;
  }
  if (inputType === InputType.Feedback) {
    const updatedAnswers = [...currentAnswers];
    const indexOfExistingAnswer = updatedAnswers.findIndex(
      (answer) => answer.questionId === question.id
    );
    if (indexOfExistingAnswer > -1) {
      updatedAnswers[indexOfExistingAnswer].value = stringValue;
    } else {
      updatedAnswers.push({
        id: uuid(),
        questionId: question.id,
        value: stringValue,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    setAnswers(updatedAnswers);
    return updatedAnswers;
  }
  return currentAnswers;
};

export const answerIsSelected = (
  answerOption: AnswerOption,
  currentAnswers: AnswerOption[]
) => {
  return currentAnswers.some(
    (answer) =>
      answer.questionId === answerOption.questionId &&
      answer.value === answerOption.value
  );
};

export const answerDisplay = (
  question: QuestionAnswerOption,
  answers: AnswerOption[]
): ReactNode => {
  const inputType = question.inputTypeName;
  if (!inputType) return "N/A";
  const answersForQuestion = answers.filter(
    (answer) => answer.questionId === question.id
  );
  if (answersForQuestion.length === 0) return "N/A";
  if (inputType === InputType.Checkbox) {
    return (
      <List sx={{ listStyleType: "disc", pl: 4 }}>
        {answersForQuestion.map((answer) => (
          <ListItem sx={{ display: "list-item" }}>
            {answer.anchor ?? "N/A "}
          </ListItem>
        ))}
      </List>
    );
  }
  if (inputType === InputType.Radio) {
    return answersForQuestion[0].anchor;
  }
  if (inputType === InputType.Feedback) {
    return answersForQuestion[0].value ?? "N/A";
  }
};

export const stripUnnecessaryMarkup = (helpText: string) => {
  return helpText.replace(/\\n/gm, "");
};
